import React, { useEffect, useState } from 'react';
import { Box, Button, Container, IconButton, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from '../../store';
import { PersonSearchRounded, AddRounded, DeleteRounded, ExpandMoreRounded } from '@mui/icons-material';
import { QueryDocumentSnapshot, collection, orderBy, where, getDocs, getCountFromServer, query,  Query, getFirestore, doc, getDoc, limit, endBefore, limitToLast, startAfter, startAt, deleteDoc } from 'firebase/firestore';
import employeeConverter from '../../converters/employeeConverter';
import { Employee } from '../../types/Employee';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { useSnackbar } from 'notistack';
import { PERMISSIONS_USERS } from '../../security/Permission';
import { SecureModule } from '../../security/SecureModule';

const ADMIN_ONLY = ['super', 'admin'];

type AppUser = {
  loginId: string;
  name: string;
  roles: string[];
  profile?: Employee;
};

export const ManageUsersPage = () => {
  const db = getFirestore();
  const { enqueueSnackbar } = useSnackbar();
  const authState = useAppSelector(s => s.auth);
  
  const isAuthorized = authState.URB && authState.URB.roles.some((r: string) => ADMIN_ONLY.includes(r));

  const [users, setUsers] = useState<AppUser[]>([]);
  const [search, setSearch] = useState('');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const [firstRow, setFirsRow] = useState<QueryDocumentSnapshot | null>(null);
  const [lastRow, setLastRow] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = (callback?: (q: Query) => Query) => {
    setLoading(true);
    let q = query(collection(db, `Admins`));

    if (search.trim() !== '')
      q = query(q, where('loginId', '>=', search), where('loginId', '<=', search + '\uf8ff'), orderBy('loginId', 'asc'));

    if(callback)
      q = callback(q);

    getDocs(q).then(qSnapshot => {
      Promise.allSettled(qSnapshot.docs.map(async d => {
        const data = d.data();
        const eDoc = await getDoc(doc(db, `Employees/${d.id}`).withConverter(employeeConverter));
        if (eDoc.exists()) {
          const eData = eDoc.data();
          return {
            loginId: d.id,
            name: eData.name,
            roles: data.roles,
            profile: eData,
          } as AppUser;
        }
        return {
          loginId: d.id,
          name: d.id,
          roles: data.roles,
        } as AppUser;
      })).then(results => {
        return (results as PromiseFulfilledResult<AppUser>[]).map(v => v.value);
      }).then(results => {
        setUsers(results);
        setFirsRow(qSnapshot.docs[0]);
        setLastRow(qSnapshot.docs[qSnapshot.docs.length - 1]);
        setLoading(false);
      });
    });

    // Count query
    let qCount: Query = query(collection(db, `Admins`));
    if (search.trim() !== '')
      qCount = query(qCount, where('loginId', '>=', search), where('loginId', '<=', search + '\uf8ff'));
    getCountFromServer(qCount).then(f => setTotalRows(f.data().count));
  };

  const handleRemoveUserOnClick = (u: AppUser) => {
    if (isAuthorized) {
      if (window.confirm(`Are you sure you want to remove ${u.loginId} as a user?`)) {
        deleteDoc(doc(db, `Admins/${u.loginId}`)).then(() => {
          enqueueSnackbar(`User '${u.loginId}' has been removed!`, { variant: 'success' });
          setPage(0);
          refresh(q => query(q, limit(rowsPerPage)));
        });
      }
    }
  };
  
  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handlePageOnChange = (_e: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    if (to > page) {
      // Next
      refresh(q => {
        return query(q, startAfter(lastRow), limit(rowsPerPage));
      });
    } else if (to < page) {
      // Previous
      refresh(q => {
        return query(q, endBefore(firstRow), limitToLast(rowsPerPage));
      });
    } else {
      // Same
      refresh(q => {
        return query(q, startAt(firstRow), limit(rowsPerPage));
      });
    }
    setPage(to);
  };

  useEffect(() => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, [rowsPerPage]);

  return (
    <SecureModule permissions={PERMISSIONS_USERS}>
      <Container disableGutters>
        <Typography component="span" variant="h5" mt={2} mb={5}>Manage Users</Typography>
        <Stack direction='row' mb={1}>
          <Box flexGrow={1} />
          <Button startIcon={<AddRounded />}>Add User</Button>
        </Stack>
        <Paper elevation={4} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell width={180} align="left">Login Id</TableCell>
                  <TableCell align="left">Name</TableCell>
                  <TableCell width={150} align="center">Role</TableCell>
                  <TableCell width={72} align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u, i) => {
                  return (
                    <TableRow 
                      key={u.loginId}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      hover
                    >
                      <TableCell align="right">{(i + 1) + (page * rowsPerPage)}</TableCell>
                      <TableCell>{u.loginId}</TableCell>
                      <TableCell>{u.name}</TableCell>
                      <TableCell align='center'>
                        <Button 
                          size='small' variant='contained' 
                          sx={{ width: 100 }} 
                          endIcon={<ExpandMoreRounded />}
                          href={`${u.loginId}`}
                        >
                          <Typography variant='caption'>
                            {
                              u.roles.length === 0 
                                ? 'No Role' : u.roles.length > 1 
                                ? 'Multiple' : u.roles[0].split('.')[0]
                            }
                          </Typography>
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Tooltip title='Remove User'>
                          <span>
                            <IconButton color='error' disabled={!isAuthorized || u.roles.includes('super')} onClick={() => handleRemoveUserOnClick(u)}>
                              <DeleteRounded />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage ?? 25}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handlePageOnChange}
            onRowsPerPageChange={handleRowsPerPageOnChange}
            ActionsComponent={TablePaginationActions} />
        </Paper>
      </Container>
    </SecureModule>
  )
};
